'use client';

import React, { createContext, ReactNode, useContext } from 'react';
import { TreatmentWithConfig, getWindow } from '@surfline/web-common';
import type { UserState } from 'types/user';
import { getTreatments, type Treatments } from 'utils/treatmentHelpers';

export {
  getTreatments,
  isTreatmentWithConfig,
  parseTreatmentWithConfig,
} from 'utils/treatmentHelpers';
export type { Treatments } from 'utils/treatmentHelpers';

/**
 * Simple custom context for storing and recalling Treatments from split.io
 *
 * Split documentation:
 * https://help.split.io/hc/en-us/articles/360033469612-Segment-Targeting-Split-Feature-Flags-Using-Segment-Personas
 */
const TreatmentContext = createContext<Treatments>({});

interface TreatmentsProviderProps {
  children: ReactNode;
  value: Treatments;
}

/**
 * Provider to be used for storing and sharing treatments across the application
 * const treatments = await getTreatments(['treatment1', 'treatment2'], user, anonymousID)
 * <TreatmentsProvider value={treatments}>
 *   children components needing access to treatments
 * </TreatmentsProvider>
 */
export const TreatmentsProvider = ({ children, value }: TreatmentsProviderProps) => (
  <TreatmentContext.Provider value={value}>{children}</TreatmentContext.Provider>
);

interface TreatmentsConsumerProps {
  children: any;
}

/**
 * To be used for Class Components needing access to treatment values
 * <TreatmentsConsumer>
 *   {(treatments: Treatments) => (
 *     <>{treatments['treatmentName']}</>
 *   )}
 * </TreatmentsConsumer>
 */
export const TreatmentsConsumer = ({ children }: TreatmentsConsumerProps) => (
  <TreatmentContext.Consumer>
    {(context) => {
      if (context === undefined) {
        throw new Error('TreatmentsConsumer must be used within a TreatmentsProvider');
      }
      return children(context);
    }}
  </TreatmentContext.Consumer>
);

/**
 * Simple hook to retrive treatments from TreatmentProvider in Functional Components
 * const treatments = useTreatments();
 * {treatments['treatmentName'] === 'on' && <>render content</>}
 */
export const useTreatments = () => {
  const context = useContext(TreatmentContext);
  if (context === undefined) {
    throw new Error('useTreatments must be used within a TreatmentsProvider');
  }
  return context;
};

/**
 * Gets a single treatment value from window.__WAVETRAK_TREATMENTS__
 * This is a synchronous client-side utility for reading treatment values
 * @param treatmentKey - The treatment key to look up
 * @returns The treatment value as a string, or undefined if not found
 */
export const getTreatmentFromWindow = (treatmentKey: string): string | undefined => {
  const windowTreatments =
    (getWindow()?.__WAVETRAK_TREATMENTS__ as Record<string, string | TreatmentWithConfig>) || {};
  const treatment = windowTreatments[treatmentKey];

  if (typeof treatment === 'string') {
    return treatment;
  }
  if (treatment && typeof treatment === 'object' && 'treatment' in treatment) {
    return (treatment as TreatmentWithConfig).treatment;
  }

  return undefined;
};

/**
 * Gets treatment values with query parameter overrides
 * @param query - Query parameters object
 * @param treatmentMappings - Object mapping treatment keys to their supported variants
 * @param splitClient - Split.io client
 * @param userState - User state object
 * @param anonymousId - Anonymous user ID
 * @param isServer - Whether this is running on the server
 * @returns Object containing treatment values
 */
export const getTreatmentsWithQueryParamOverride = async (
  query: Record<string, string>,
  treatmentMappings: Record<string, string[]>,
  splitClient: any,
  userState: UserState | null,
  anonymousId?: string,
  isServer?: boolean,
): Promise<Record<string, string>> => {
  const treatmentKeys = Object.keys(treatmentMappings);

  // First check query parameters
  const queryResults = treatmentKeys.reduce(
    (acc, key) => {
      const queryValue = query[key];

      if (queryValue && treatmentMappings[key].includes(queryValue)) {
        acc[key] = queryValue;
      }
      return acc;
    },
    {} as Record<string, string>,
  );

  // If all treatments are overridden by query params, return early
  if (Object.keys(queryResults).length === treatmentKeys.length) {
    return queryResults;
  }

  // Get any remaining treatments that weren't overridden by query params
  const remainingKeys = treatmentKeys.filter((key) => !queryResults[key]);
  if (remainingKeys.length === 0) {
    return queryResults;
  }

  let treatments: Record<string, string> = {};

  if (isServer && splitClient) {
    // On server, make a single call to getTreatments for all remaining keys
    const serverTreatments = await getTreatments(
      splitClient,
      remainingKeys,
      userState,
      anonymousId,
    );
    // Convert treatments to string values
    treatments = Object.entries(serverTreatments).reduce(
      (acc, [key, value]) => {
        if (typeof value === 'string') {
          acc[key] = value;
        } else if (value && typeof value === 'object' && 'treatment' in value) {
          acc[key] = (value as TreatmentWithConfig).treatment;
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  } else {
    // On client, use the treatments from window
    const windowTreatments =
      (getWindow()?.__WAVETRAK_TREATMENTS__ as Record<string, string | TreatmentWithConfig>) || {};

    treatments = remainingKeys.reduce(
      (acc, key) => {
        const treatment = windowTreatments[key];
        if (typeof treatment === 'string') {
          acc[key] = treatment;
        } else if (treatment && typeof treatment === 'object' && 'treatment' in treatment) {
          acc[key] = (treatment as TreatmentWithConfig).treatment;
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  }

  // Add remaining treatments to result
  const remainingResults = remainingKeys.reduce(
    (acc, key) => {
      if (treatments[key]) {
        acc[key] = treatments[key];
      }
      return acc;
    },
    {} as Record<string, string>,
  );

  return { ...queryResults, ...remainingResults };
};

const defaultExport = {
  TreatmentsConsumer,
  TreatmentsProvider,
  getTreatments,
  getTreatmentsWithQueryParamOverride,
  useTreatments,
  getTreatmentFromWindow,
};

export default defaultExport;
